import React, { useEffect, useState } from "react";
import {
  Row,
  Label,
  Input,
  FormFeedback,
  Form,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import * as Yup from "yup";
import Select from "react-select";
import { useFormik } from "formik";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import {
  ErrorHandlingTypeEnum,
  NotificationAudienceEnum,
  NotificationMethodEnum,
} from "../../enums";
import { ActivityButton } from "../../components/Common";
import {
  LocalizedProperty,
  ParsedError,
  SendNotificationRequest,
} from "../../interfaces";
import { useSendNotificationMutation } from "../../store/api/notificationApi";
import { TemplateSelect } from "../../components";
import { TemplateState } from "../../store/slices/templateSlice";
import { toast } from "react-toastify";

const APPLICATION_AUDIENCES = [
  NotificationAudienceEnum.STUDENT,
  NotificationAudienceEnum.PARENT,
];

const audienceGroupOf = (value: NotificationAudienceEnum) =>
  APPLICATION_AUDIENCES.includes(value) ? "application" : "administration";

type TProps = {
  mode: "role" | "grade" | "complain" | "user";
  role?: { id: string; name: LocalizedProperty }[];
  grade?: { id: string; name: LocalizedProperty }[];
  complain?: {
    id: string;
    message: string;
  };
  user?: { id: string; name: LocalizedProperty }[];
  show: boolean;
  onCloseClick: (event: any) => void;
};
export const SendPushNotificationModal: React.FC<TProps> = ({
  mode,
  grade,
  complain,
  user,
  show,
  onCloseClick,
}) => {
  const { t, i18n } = useTranslation();

  //#region internal state
  const [sendMethod, setSendMethod] = useState<NotificationMethodEnum>(
    NotificationMethodEnum.PUSH_NOTIFICATION,
  );
  const [selectedTemplate, setSelectedTemplate] = useState<any>();
  //#endregion

  //#region redux state
  const templates = useSelector(
    (state: { template: TemplateState }) => state.template.templates,
  );
  //#endregion

  const roleOptions = [
    {
      label: t("appUsers"),
      options: [
        { label: t("students"), value: NotificationAudienceEnum.STUDENT },
        { label: t("parents"), value: NotificationAudienceEnum.PARENT },
      ],
    },
    {
      label: t("cpUsers"),
      options: [
        {
          label: t("systemAdmins"),
          value: NotificationAudienceEnum.SYSTEM_ADMIN,
        },
        {
          label: t("headTeacherAdmins"),
          value: NotificationAudienceEnum.HEAD_TEACHER_ADMIN,
        },
        {
          label: t("teacherAdmins"),
          value: NotificationAudienceEnum.TEACHER_ADMIN,
        },
      ],
    },
  ];

  //#region notification
  const [
    sendNotification,
    {
      isLoading: isLoading,
      error: error,
      isError: isError,
      reset: resetSendNotification,
    },
  ] = useSendNotificationMutation();

  /**
   * Formik handling
   */
  const notificationForm = useFormik<SendNotificationRequest>({
    enableReinitialize: true,
    initialValues: {
      audience:
        mode === "role"
          ? []
          : mode === "grade"
            ? [NotificationAudienceEnum.GRADE]
            : [NotificationAudienceEnum.USER],
      audienceIds:
        mode === "grade"
          ? grade?.map((g) => g.id)
          : mode === "user"
            ? user?.map((u) => u.id)
            : undefined,
      complainId: mode === "complain" ? complain?.id : undefined,
      title: "",
      arabicTitle: "",
      body: "",
      arabicBody: "",
      method: NotificationMethodEnum.PUSH_NOTIFICATION,
      templateId: null,
    },
    validationSchema: Yup.object<SendNotificationRequest>({
      ...(mode === "role" && {
        audience: Yup.array()
          .min(
            1,
            t("minLengthArray", { minLengthArray: 1, displayName: "role" }),
          )
          .required(t("isNotEmpty", { displayName: "role" })),
      }),
      ...(mode === "grade" && {
        audienceIds: Yup.array()
          .min(
            1,
            t("minLengthArray", { minLengthArray: 1, displayName: "grade" }),
          )
          .required(t("isNotEmpty", { displayName: "grade" })),
      }),
      ...(mode === "complain" && {
        complainId: Yup.string().required(
          t("isNotEmpty", { displayName: "complain" }),
        ),
      }),
      ...(mode === "user" && {
        audienceIds: Yup.array()
          .min(
            1,
            t("minLengthArray", { minLengthArray: 1, displayName: "user" }),
          )
          .required(t("isNotEmpty", { displayName: "user" })),
      }),
      title: Yup.string().when("method", {
        is: NotificationMethodEnum.PUSH_NOTIFICATION,
        then: (schema) =>
          schema
            .required(t("isNotEmpty", { displayName: "title" }))
            .max(50, t("maxLength", { maxLength: 50, displayName: "title" })),
      }),
      arabicTitle: Yup.string().when("method", {
        is: NotificationMethodEnum.PUSH_NOTIFICATION,
        then: (schema) =>
          schema
            .required(t("isNotEmpty", { displayName: "arabicTitle" }))
            .max(
              50,
              t("maxLength", { maxLength: 50, displayName: "arabicTitle" }),
            ),
      }),
      body: Yup.string().when("method", {
        is: NotificationMethodEnum.PUSH_NOTIFICATION,
        then: (schema) =>
          schema
            .required(t("isNotEmpty", { displayName: "body" }))
            .max(255, t("maxLength", { maxLength: 255, displayName: "body" })),
      }),
      arabicBody: Yup.string().when("method", {
        is: NotificationMethodEnum.PUSH_NOTIFICATION,
        then: (schema) =>
          schema
            .required(t("isNotEmpty", { displayName: "arabicBody" }))
            .max(
              255,
              t("maxLength", { maxLength: 255, displayName: "arabicBody" }),
            ),
      }),
      method: Yup.string().required(t("isNotEmpty", { displayName: "method" })),
      templateId: Yup.string().when("method", {
        is: NotificationMethodEnum.WHATSAPP,
        then: (schema) =>
          schema.required(t("isNotEmpty", { displayName: "template" })),
        otherwise: (schema) => schema.nullable().notRequired(),
      }),
    }),
    onSubmit: async (values) => {
      if (!values.method) return;
      if (
        values.method === NotificationMethodEnum.WHATSAPP &&
        !values.templateId
      ) {
        return;
      }

      const payload: SendNotificationRequest = {
        title: values.title,
        arabicTitle: values.arabicTitle,
        body: values.body,
        arabicBody: values.arabicBody,
        method: sendMethod,
        templateId:
          sendMethod === NotificationMethodEnum.WHATSAPP
            ? values.templateId
            : null,
        scheduleDate: new Date(),
        audience: (values.audience || []).filter(Boolean),
        ...(values.audienceIds?.length
          ? { audienceIds: values.audienceIds }
          : {}),
        ...(values.complainId ? { complainId: values.complainId } : {}),
      };

      try {
        await sendNotification(payload).unwrap();
        toast.success(t("notificationSentSuccessfully"), {
          autoClose: 3000,
        });
        onClose();
      } catch {
        // Form errors are applied from the mutation error effect.
      }
    },
  });

  useEffect(() => {
    let _error: ParsedError | null = null;
    if (!isLoading && isError) _error = error as ParsedError;
    if (_error?.type == ErrorHandlingTypeEnum.FORM) {
      notificationForm.setErrors(_error.error);
    }
  }, [isLoading, isError]);

  useEffect(() => {
    notificationForm.setFieldValue("method", sendMethod);
  }, [sendMethod]);

  useEffect(() => {
    if (sendMethod === NotificationMethodEnum.WHATSAPP) {
      const _chooseTemplate = templates?.find(
        (t) => t.sid === selectedTemplate?.value,
      );
      if (_chooseTemplate?.types["twilio/text"]) {
        notificationForm.setFieldValue(
          "body",
          _chooseTemplate?.types["twilio/text"]["body"],
        );
      }
      if (_chooseTemplate?.types["twilio/call-to-action"]) {
        notificationForm.setFieldValue(
          "body",
          _chooseTemplate?.types["twilio/call-to-action"]["body"],
        );
      }
      notificationForm.setFieldValue("templateId", _chooseTemplate?.sid);
      return;
    }

    notificationForm.setFieldValue("templateId", null);
    notificationForm.setFieldTouched("templateId", false);
  }, [selectedTemplate, sendMethod]);

  //#endregion

  const onClose = () => {
    notificationForm.resetForm();
    setSelectedTemplate(undefined);
    setSendMethod(NotificationMethodEnum.PUSH_NOTIFICATION);
    resetSendNotification();
    onCloseClick(!show);
  };

  return (
    <React.Fragment>
      <Modal
        id="pushNotification"
        isOpen={show}
        toggle={() => {
          onClose();
        }}
        centered
        size="lg"
      >
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            notificationForm.handleSubmit();
            return false;
          }}
          action="#"
        >
          <ModalHeader
            className="p-3"
            toggle={() => {
              onClose();
            }}
          >
            {t("sendNotification")}
          </ModalHeader>

          <ModalBody>
            {mode === "role" && (
              <Row>
                <div className="mb-3 col">
                  <Label htmlFor="title" className="form-label">
                    {t("role")}
                    <span className="text-danger">*</span>
                  </Label>
                  <Select
                    id="role"
                    placeholder={t("chooseRole")}
                    closeMenuOnSelect={false}
                    value={roleOptions
                      .flatMap((group) => group.options)
                      .filter((option) =>
                        notificationForm.values.audience?.includes(
                          option.value,
                        ),
                      )}
                    onChange={(changedValue) => {
                      const selected = (changedValue || []).map(
                        (option: { value: NotificationAudienceEnum }) =>
                          option.value,
                      );
                      const previous =
                        notificationForm.values.audience || [];
                      const added = selected.find(
                        (value) => !previous.includes(value),
                      );
                      const next =
                        added && selected.some(
                          (value) =>
                            audienceGroupOf(value) !==
                            audienceGroupOf(added),
                        )
                          ? selected.filter(
                              (value) =>
                                audienceGroupOf(value) ===
                                audienceGroupOf(added),
                            )
                          : selected;
                      notificationForm.setFieldValue("audience", next);
                    }}
                    isOptionDisabled={(option: {
                      value: NotificationAudienceEnum;
                    }) => {
                      const selected = (notificationForm.values.audience ||
                        []) as NotificationAudienceEnum[];
                      if (!selected.length) return false;
                      return (
                        audienceGroupOf(option.value) !==
                        audienceGroupOf(selected[0])
                      );
                    }}
                    isMulti={true}
                    options={roleOptions}
                  />
                  {notificationForm.errors.audience ? (
                    <FormFeedback type="invalid" style={{ display: "block" }}>
                      {notificationForm.errors.audience}
                    </FormFeedback>
                  ) : null}
                </div>
              </Row>
            )}
            {mode === "grade" && (
              <p className="mb-3 fs-14">
                <strong> {t("selectedGrades")}:&nbsp;</strong>
                {grade?.map((g) =>
                  i18n.language === "ar" ? g.name.ar : g.name.en,
                )}
              </p>
            )}
            {mode === "complain" && (
              <p className="mb-3 fs-14">
                <strong>{t("replyingToMessage")}:&nbsp;</strong>
                {complain?.message}
              </p>
            )}
            {mode === "user" && (
              <p className="mb-3 fs-14">
                <strong>{t("targetUsers")}:&nbsp;</strong>
                {user
                  ?.map((u) =>
                    i18n.language === "ar" ? u.name.ar : u.name.en || t(" - "),
                  )
                  .join(", ")}
              </p>
            )}
            <Row>
              <div className="mb-3 col">
                <div>
                  <div className="form-check form-check-inline">
                    <Input
                      className="form-check-input"
                      type="radio"
                      name="inlineRadio3"
                      id="inlineRadio3"
                      value={NotificationMethodEnum.PUSH_NOTIFICATION}
                      checked={
                        sendMethod == NotificationMethodEnum.PUSH_NOTIFICATION
                      }
                      onChange={() => {
                        setSendMethod(NotificationMethodEnum.PUSH_NOTIFICATION);
                      }}
                    />
                    <label className="form-check-label" htmlFor="inlineRadio3">
                      {t(NotificationMethodEnum.PUSH_NOTIFICATION)}
                    </label>
                  </div>
                </div>
              </div>
            </Row>
            <Row>
              {sendMethod === NotificationMethodEnum.WHATSAPP && (
                <div className="mb-3 col">
                  <TemplateSelect
                    placeholder="chooseTemplate"
                    isMulti={false}
                    closeMenuOnSelect={true}
                    value={selectedTemplate}
                    onChange={(newValue) => {
                      setSelectedTemplate(newValue);
                    }}
                  ></TemplateSelect>
                </div>
              )}
            </Row>
            {sendMethod != NotificationMethodEnum.WHATSAPP && (
              <>
                <Row>
                  <div className="mb-3 col">
                    <Label htmlFor="title" className="form-label">
                      {t("title")}
                      <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="text"
                      className="form-control"
                      id="title"
                      placeholder={t("enterTitle")}
                      value={notificationForm.values.title || ""}
                      onChange={notificationForm.handleChange}
                      onBlur={notificationForm.handleBlur}
                      invalid={
                        notificationForm.touched.title &&
                        notificationForm.errors.title
                          ? true
                          : false
                      }
                    />
                    {notificationForm.touched.title &&
                    notificationForm.errors.title ? (
                      <FormFeedback type="invalid">
                        {notificationForm.errors.title}
                      </FormFeedback>
                    ) : null}
                  </div>
                </Row>
                <Row>
                  <div className="mb-3 col">
                    <Label htmlFor="arabicTitle" className="form-label">
                      {t("arabicTitle")}
                      <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="text"
                      className="form-control"
                      id="arabicTitle"
                      placeholder={t("enterArabicTitle")}
                      value={notificationForm.values.arabicTitle || ""}
                      onChange={notificationForm.handleChange}
                      onBlur={notificationForm.handleBlur}
                      invalid={
                        notificationForm.touched.arabicTitle &&
                        notificationForm.errors.arabicTitle
                          ? true
                          : false
                      }
                    />
                    {notificationForm.touched.arabicTitle &&
                    notificationForm.errors.arabicTitle ? (
                      <FormFeedback type="invalid">
                        {notificationForm.errors.arabicTitle}
                      </FormFeedback>
                    ) : null}
                  </div>
                </Row>
              </>
            )}
            <Row>
              <div className="mb-3 col">
                <Label htmlFor="body" className="form-label">
                  {sendMethod === NotificationMethodEnum.WHATSAPP
                    ? t("Template")
                    : t("body")}
                  {sendMethod != NotificationMethodEnum.WHATSAPP && (
                    <span className="text-danger">*</span>
                  )}
                </Label>
                <Input
                  disabled={sendMethod === NotificationMethodEnum.WHATSAPP}
                  type="textarea"
                  row="5"
                  style={{ height: "150px" }}
                  className="form-control"
                  id="body"
                  placeholder={
                    sendMethod === NotificationMethodEnum.WHATSAPP
                      ? ""
                      : t("enterMessageBody")
                  }
                  value={notificationForm.values.body || ""}
                  onChange={notificationForm.handleChange}
                  onBlur={notificationForm.handleBlur}
                  invalid={
                    notificationForm.touched.body &&
                    notificationForm.errors.body
                      ? true
                      : false
                  }
                />
                {notificationForm.touched.body &&
                notificationForm.errors.body ? (
                  <FormFeedback type="invalid">
                    {notificationForm.errors.body}
                  </FormFeedback>
                ) : null}
              </div>
            </Row>
            {sendMethod != NotificationMethodEnum.WHATSAPP && (
              <Row>
                <div className="mb-3 col">
                  <Label htmlFor="arabicBody" className="form-label">
                    {t("arabicBody")}
                    <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="textarea"
                    row="5"
                    className="form-control"
                    style={{ height: "150px" }}
                    id="arabicBody"
                    placeholder={t("enterMessageBodyInArabic")}
                    value={notificationForm.values.arabicBody || ""}
                    onChange={notificationForm.handleChange}
                    onBlur={notificationForm.handleBlur}
                    invalid={
                      notificationForm.touched.arabicBody &&
                      notificationForm.errors.arabicBody
                        ? true
                        : false
                    }
                  />
                  {notificationForm.touched.arabicBody &&
                  notificationForm.errors.arabicBody ? (
                    <FormFeedback type="invalid">
                      {notificationForm.errors.arabicBody}
                    </FormFeedback>
                  ) : null}
                </div>
              </Row>
            )}
          </ModalBody>
          <ModalFooter>
            <Link
              to={"#"}
              onClick={onClose}
              className="btn btn-link link-success fw-medium"
            >
              <i className="ri-close-line me-1 align-middle"></i> {t("close")}
            </Link>
            <ActivityButton
              title={t("send")}
              inProgressTitle="saving"
              color="success"
              className="btn btn-success"
              type="submit"
              inProgress={isLoading}
              disabled={isLoading}
            ></ActivityButton>
          </ModalFooter>
        </Form>
      </Modal>
    </React.Fragment>
  );
};
